import { FormEvent, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { PATH } from '../../constants/path'

import styles from './ResetPassword.module.css'
import { AppDispatch } from '../../redux/store'
import { useDispatch } from 'react-redux'
import {
  fetchForgotPassword,
  fetchResetPassword,
} from '../../redux/reducers/auth'
import Footer from '../../components/footer/Footer'

const RESEND_COOLDOWN_SECONDS = 60

function ResetPassword() {
  const dispatch = useDispatch<AppDispatch>()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const token = searchParams.get('token') || ''
  const emailFromLink = searchParams.get('email') || ''
  const isResetMode = Boolean(token && emailFromLink)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSent, setIsSent] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (cooldown <= 0) return

    const timer = window.setInterval(() => {
      setCooldown((prev) => (prev <= 1 ? 0 : prev - 1))
    }, 1000)

    return () => window.clearInterval(timer)
  }, [cooldown])

  const handleForgotSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (isSubmitting || cooldown > 0) return

    setErrorMessage('')
    setIsSubmitting(true)
    try {
      await dispatch(fetchForgotPassword(email)).unwrap()
      setIsSent(true)
      setCooldown(RESEND_COOLDOWN_SECONDS)
    } catch (error) {
      setIsSent(false)
      setErrorMessage(
        typeof error === 'string' && error.trim()
          ? error
          : 'Ошибка при отправке данных'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResetSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return

    setErrorMessage('')
    setIsSubmitting(true)
    try {
      await dispatch(
        fetchResetPassword({
          token,
          email: emailFromLink,
          password,
          password_confirmation: passwordConfirmation,
        })
      ).unwrap()
    } catch (error) {
      setErrorMessage(
        typeof error === 'string' && error.trim()
          ? error
          : 'Ошибка при сбросе пароля'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const getForgotButtonText = () => {
    if (isSubmitting) return 'Отправка...'
    if (cooldown > 0) return `Отправить повторно (${cooldown} с)`
    return isSent ? 'Отправить повторно' : 'Отправить'
  }

  return (
    <div
      className={`${styles.block} ${styles.block1} ${styles.bgMonoh} ${styles.bgBlue}`}
      style={{ backgroundImage: 'url(img/logo/slide09.jpg)' }}
    >
      <div className={`container ${styles.container}`}>
        <div className="row">
          <div className="col-12 offset-sm-2 col-sm-8 offset-md-2 col-md-8 offset-lg-3 col-lg-6">
            <div className="mb-5" style={{ textAlign: 'center' }}>
              <a href="login.php" className={`${styles.logo} d-block mb-2`}>
                <img width="240" src="img/logo/logo-white.svg" alt="logo" />
              </a>
              <div className="mb-0">
                <b className={styles.b}>Система управления логистикой</b>
              </div>
            </div>

            <div
              className={`${styles.blockWhite} ${styles.borderRadius20}`}
              style={{ textAlign: 'left' }}
            >
              <h1
                className={`${styles.h2} ${styles.wow} ${styles.fadeIn}`}
                style={{ textAlign: 'center' }}
              >
                {isResetMode ? 'Новый пароль' : 'Восстановление пароля'}
              </h1>

              {isResetMode ? (
                <form onSubmit={handleResetSubmit} className="mb-5">
                  <div className={styles.formLine}>
                    <small>Новый пароль</small>
                    <input
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value)
                        if (errorMessage) setErrorMessage('')
                      }}
                      required
                      type="password"
                      name="password"
                      placeholder="Введите новый пароль"
                      autoComplete="new-password"
                    />
                  </div>
                  <div className={styles.formLine}>
                    <small>Подтверждение пароля</small>
                    <input
                      value={passwordConfirmation}
                      onChange={(e) => {
                        setPasswordConfirmation(e.target.value)
                        if (errorMessage) setErrorMessage('')
                      }}
                      required
                      type="password"
                      name="password_confirmation"
                      placeholder="Повторите новый пароль"
                      autoComplete="new-password"
                    />
                  </div>
                  {errorMessage ? (
                    <div className="alert alert-danger" role="alert">
                      {errorMessage}
                    </div>
                  ) : null}
                  <div style={{ textAlign: 'center' }}>
                    <input
                      type="submit"
                      disabled={isSubmitting}
                      className={`${styles.btn} d-block d-sm-inline-block`}
                      value={isSubmitting ? 'Сохранение...' : 'Сохранить'}
                    />
                  </div>
                </form>
              ) : (
                <form onSubmit={handleForgotSubmit} className="mb-5">
                  <div className={styles.formLine}>
                    <small>E-mail / Login</small>
                    <input
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value)
                        setIsSent(false)
                        if (errorMessage) setErrorMessage('')
                      }}
                      required
                      disabled={isSubmitting}
                      type="text"
                      name="email"
                      placeholder="Введите ваш E-mail или Login"
                      autoComplete="username"
                    />
                  </div>
                  {errorMessage ? (
                    <div className="alert alert-danger" role="alert">
                      {errorMessage}
                    </div>
                  ) : isSent ? (
                    <div className="alert alert-success" role="alert">
                      Письмо со ссылкой для сброса пароля отправлено на вашу
                      электронную почту.{' '}
                      {cooldown > 0
                        ? `Повторная отправка будет доступна через ${cooldown} сек.`
                        : 'Вы можете запросить ссылку повторно.'}
                    </div>
                  ) : (
                    <p className={styles.small}>
                      Мы отправим вам ссылку для сброса пароля на электронную
                      почту.
                    </p>
                  )}
                  <div style={{ textAlign: 'center' }}>
                    <input
                      type="submit"
                      disabled={isSubmitting || cooldown > 0}
                      className={`${styles.btn} d-block d-sm-inline-block`}
                      value={getForgotButtonText()}
                    />
                  </div>
                </form>
              )}
            </div>

            <div
              style={{ textAlign: 'center' }}
              className={`${styles.small} mt-5`}
            >
              <Link className={styles.colorWhite} to={PATH.AUTH}>
                <b>Вернуться к Входу</b>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default ResetPassword
